import {
  RECOMMENDATION_REQUEST_START,
  RECOMMENDATION_REQUEST_SUCCESS,
  RECOMMENDATION_REQUEST_FAILED,
  RECOMMENDATION_CONTENT_START,
  RECOMMENDATION_CONTENT_SUCCESS,
  RECOMMENDATION_CONTENT_FAILED,
  RECOMMENDATION_INDIVIDUAL_REQUEST_FAILED,
  RECOMMENDATION_INDIVIDUAL_REQUEST_START,
  RECOMMENDATION_INDIVIDUAL_REQUEST_SUCCESS,
  CLEAR_RECOMMENDATION,
  SET_DOB_ERROR,
  RecommendationResponsePayload,
  RECOMMENDATION_CONFIG_SUCCESS,
  RECOMMENDATION_CONFIG_FAILED,
  RECOMMENDATION_CONFIG_START
} from './types';
import { QUOTE_REQUEST_FAILED } from '../quote/types';
import { Dispatch } from 'redux';
import { flatMap, map, first, toUpper, toString } from 'lodash';
import { ReduxStore } from '../payload-types';
import { ApiClient } from '../api.client';
import { LINKS, ORGANIZATION_CONTRACT_WITH_CONFIG_QUERY, FEATURE_FLAGS } from '@trip-ux/constants';
import { prepareRecommendation, prepareIndividual, prepareRecommendationForPolicyInfo } from '../request.mappers';
import { getQuote, getQuoteForPolicyInfo } from '../quote';
import { checkFeatureFlag } from '../utils';

const useDestinationPricing = (state: ReduxStore) =>
  checkFeatureFlag(state.userProfile?.featureFlags, FEATURE_FLAGS.DESTINATION_PRICING);

const handleRecommendationError = (dispatch: Dispatch, payload, error) => {
  dispatch({
    type: RECOMMENDATION_REQUEST_FAILED,
    payload: map(payload.paxes, pax => ({
      error,
      loading: false,
      referenceNumber: pax.referenceNumber
    }))
  });
  // Also dispatch quote failure so the UI stops showing the loader
  // When recommendation fails, quote cannot proceed, so we mark it as failed too
  dispatch({
    type: QUOTE_REQUEST_FAILED,
    payload: map(payload.paxes, pax => ({
      error,
      loading: false,
      referenceNumber: pax.referenceNumber
    }))
  });
};

export const getRecommendation =
  () =>
  (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient): Promise<void> => {
    const payload = prepareRecommendation(getState());
    dispatch({
      type: RECOMMENDATION_REQUEST_START,
      payload: flatMap(payload.paxes, 'referenceNumber')
    });
    return apiClient(dispatch, getState)
      .post<RecommendationResponsePayload[]>(LINKS.RECOMMENDATION, payload, {
        headers: { ...(useDestinationPricing(getState()) && { 'api-version': '2' }) }
      })
      .then(data => {
        dispatch({ type: RECOMMENDATION_REQUEST_SUCCESS, payload: data });
        getQuote()(dispatch, getState, apiClient);
      })
      .catch(e => {
        handleRecommendationError(dispatch, payload, e);
      });
  };

export const getRecommendationForPolicyInfo =
  () =>
  (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient): Promise<void> => {
    const payload = prepareRecommendationForPolicyInfo(getState());
    dispatch({
      type: RECOMMENDATION_REQUEST_START,
      payload: flatMap(payload.paxes, 'referenceNumber')
    });
    return apiClient(dispatch, getState)
      .post<RecommendationResponsePayload[]>(LINKS.RECOMMENDATION, payload, {
        headers: { ...(useDestinationPricing(getState()) && { 'api-version': '2' }) }
      })
      .then(data => {
        dispatch({ type: RECOMMENDATION_REQUEST_SUCCESS, payload: data });
        getQuoteForPolicyInfo()(dispatch, getState, apiClient);
      })
      .catch(e => {
        handleRecommendationError(dispatch, payload, e);
      });
  };

export const getContent =
  (organizationCode: string, effectiveDate: Date, lang: string, salesChannel: string, contextPath?: string) =>
  (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient): Promise<void> => {
    dispatch({
      type: RECOMMENDATION_CONTENT_START,
      params: {
        organizationCode,
        effectiveDate,
        language: toUpper(lang),
        salesChannel: toUpper(salesChannel)
      }
    });
    return apiClient(dispatch, getState)
      .get(LINKS.PRODUCT, {
        params: { organizationCode, effectiveDate, language: toUpper(lang), salesChannel: toUpper(salesChannel) }
      })
      .then(res => {
        dispatch({ type: RECOMMENDATION_CONTENT_SUCCESS, payload: { res, contextPath } });
      })
      .catch(e => {
        dispatch({ type: RECOMMENDATION_CONTENT_FAILED, payload: e });
      });
  };

export const getPlanConfig =
  (organizationCode: string, effectiveDate: Date, lang: string, salesChannel: string) =>
  (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient): Promise<void> => {
    dispatch({ type: RECOMMENDATION_CONFIG_START });
    return apiClient(dispatch, getState)
      .post(LINKS.GRAPHQL, {
        query: ORGANIZATION_CONTRACT_WITH_CONFIG_QUERY,
        variables: { effectiveDate, lang: toUpper(lang), salesChannel: toUpper(salesChannel) }
      })
      .then(({ data }) => {
        const fullPayload = {
          productConfigs: data.organizationContractProductConfigurations,
          organization: {
            code: organizationCode,
            organizationName: 'CDTTRIPX-8946',
            organizationNameInFrench: 'CDTTRIPX-8946'
          }
        };
        dispatch({ type: RECOMMENDATION_CONFIG_SUCCESS, payload: {organizationContract:fullPayload} });
      })
      .catch(e => {
        dispatch({ type: RECOMMENDATION_CONFIG_FAILED, payload: e });
      });
  };

export const getIndividualRecommendation =
  (paxId: number) =>
  (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient): Promise<void> => {
    dispatch({
      type: RECOMMENDATION_INDIVIDUAL_REQUEST_START,
      payload: paxId
    });
    const payload = prepareRecommendation(getState());
    return apiClient(dispatch, getState)
      .post<RecommendationResponsePayload[]>(LINKS.RECOMMENDATION, prepareIndividual(payload, paxId), {
        headers: { ...(useDestinationPricing(getState()) && { 'api-version': '2' }) }
      })
      .then(data => {
        dispatch({
          type: RECOMMENDATION_INDIVIDUAL_REQUEST_SUCCESS,
          payload: {
            referenceNumber: paxId,
            recommendationData: first(data as unknown as RecommendationResponsePayload[]),
            loading: false
          }
        });
        // Only trigger quote when no other recommendations are still loading
        const { recommendation } = getState();
        const anyStillLoading = Object.values(recommendation).some(
          r => typeof r === 'object' && r !== null && (r as { loading?: boolean }).loading
        );
        if (!anyStillLoading) {
          getQuote()(dispatch, getState, apiClient);
        }
      })
      .catch(e => {
        dispatch({
          type: RECOMMENDATION_INDIVIDUAL_REQUEST_FAILED,
          payload: { referenceNumber: toString(paxId), loading: false, error: e }
        });
      });
  };

export const clearRecommendation = () => (dispatch: Dispatch) => {
  dispatch({
    type: CLEAR_RECOMMENDATION
  });
};

export const setDobError = (paxId: number, error: string) => (dispatch: Dispatch) => {
  dispatch({
    type: SET_DOB_ERROR,
    payload: { paxId, error }
  });
};
